import React, { useState, useEffect } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useStatus, isLoading, isNormal, isError } from 'resift';
import usePrevious from '@sift/resift/usePrevious';
import makeDeleteEntity from 'fetches/makeDeleteEntity';
import showSnackbar from 'helpers/showSnackbar';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import CancelButton from 'components/Page/CancelButton';
import LoadingButton from 'atoms/LoadingButton';

interface Props {
  name: string;
  open: boolean;
  subtitle: string;
  onSetOpen: (state: boolean) => void;
  onDelete: () => void;
}

function DeletePageModal({ name, open, subtitle, onSetOpen, onDelete }: Props) {
  const classes = useStyles();
  const deleteEntity = makeDeleteEntity();
  const status = useStatus(deleteEntity);
  const prevStatus = usePrevious(status);
  const [nameInput, setNameInput] = useState('');

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameInput(e.target.value);
  };

  const handleCancel = () => {
    setNameInput('');
    onSetOpen(false);
  };

  showSnackbar({
    status: status,
    message: 'Page deleted successfully',
    errorMessage: 'Page deletion was not successful. Please try again.',
    variant: 'success',
  });

  useEffect(() => {
    if (isLoading(prevStatus) && isNormal(status)) {
      onSetOpen(false);
      setNameInput('');
    } else if (isLoading(prevStatus) && isError(status)) {
      setNameInput('');
    }
  }, [status, prevStatus]);

  return (
    <Dialog data-testid="delete-page-modal" open={open} onClose={handleCancel}>
      <DialogTitle> Are you sure you want to delete "{name}" page?</DialogTitle>
      <DialogContent className={classes.marginBottom}>
        <DialogContentText>{subtitle}</DialogContentText>
        <TextField
          id="page-name-input"
          fullWidth={true}
          value={nameInput}
          inputProps={{ 'aria-label': 'page-name-input' }}
          onChange={handleNameChange}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={handleCancel} />
        <LoadingButton
          variant="contained"
          disabled={nameInput !== name}
          onClick={onDelete}
          className={classes.deleteButton}
          loading={isLoading(status)}
          label="Delete"
          spinnerClassName={classes.loadingButtonSpinner}
        />
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: any) => ({
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
  },
  marginBottom: {
    marginBottom: theme.spacing(3),
  },
  loadingButtonSpinner: {
    color: theme.palette.common.white,
  },
}));

export default DeletePageModal;
